import React, { useEffect, useMemo, useState } from 'react';

import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { NEW_CHAT_TOKEN } from '~/components/Chat/constants';
import { ChatProvider, useClaimChatsContext } from '~/components/Chat/context/ChatContext.tsx';
import { ClaimChatDocumentProvider } from '~/components/Chat/context/ChatDocumentContext.tsx';
import { ActionsProvider } from '~/components/Claim/Actions/ActionsContext';
import { ClaimProvider, useClaimContext } from '~/components/Claim/ClaimContext';
import { claimInfoToTopBar } from '~/components/Claim/ClaimInfo/helper.tsx';
import { ClaimContext as ClaimInfoContext } from '~/components/Claim/ClaimInfo/types.ts';
import { InsightsProvider } from '~/components/Claim/Insights/InsightsContext';
import { useMainTopBar } from '~/context/MainTopBarContext';
import { LINE_OF_BUSINESS } from '~/core/constants/LineOfBusiness';
import { get } from '~/utils/request';

const Claim: React.FC = () => {
  const { claimNumber } = useParams();
  const navigate = useNavigate();

  // claim info state
  const [claimInfo, setClaimInfo] = useState<ClaimInfoContext | null>(null);

  const handleChatMessage = React.useCallback(
    async (message: string) => {
      if (claimNumber) {
        navigate(`/claims/${claimNumber}/chat/${NEW_CHAT_TOKEN}`, {
          state: {
            userMessage: message,
          },
        });
      }
    },
    [claimNumber, navigate]
  );

  const fetchClaimInfo = async (claimNum: string) => {
    try {
      const response = await get<ClaimInfoContext>({
        url: `/api/v1/claims/${claimNum}/context`,
      });
      setClaimInfo(response);
    } catch (error) {
      console.error('Error fetching claim info:', error);
    }
  };

  useEffect(() => {
    if (claimNumber) {
      fetchClaimInfo(claimNumber);
    }
  }, [claimNumber]);

  return (
    <ClaimProvider claimNumber={claimNumber} claimInfo={claimInfo} handleChatMessage={handleChatMessage}>
      <ActionsProvider>
        <InsightsProvider>
          <ClaimChatDocumentProvider>
            <ChatProvider>
              <ClaimInner />
            </ChatProvider>
          </ClaimChatDocumentProvider>
        </InsightsProvider>
      </ActionsProvider>
    </ClaimProvider>
  );
};

const ClaimInner: React.FC = () => {
  const { chatTitle } = useClaimChatsContext();
  const { claimNumber, claimInfo } = useClaimContext();
  const { setMainTopBarProps } = useMainTopBar();
  const location = useLocation();

  const isChatPage = location.pathname.includes('/chat/');
  const breadcrumbItem = useMemo(
    () => (isChatPage ? { label: chatTitle || 'New Chat' } : undefined),
    [isChatPage, chatTitle]
  );

  useEffect(() => {
    setMainTopBarProps({
      claimId: claimNumber,
      lineOfBusiness: LINE_OF_BUSINESS.AUTO,
      claimInfo: claimInfoToTopBar(claimInfo),
      breadcrumbItem,
    });
  }, [claimNumber, claimInfo, breadcrumbItem, setMainTopBarProps]);

  return <Outlet />;
};

export default Claim;
